import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { useMemo } from "react";
import { CommandItem, CommandShortcut } from "@volca/ui/components/ui/command";
import {
  useGetInvoices,
  useGetReceipts,
  useGetReviewQueue,
} from "@/accounts-payable/query";
import { useReceiptsEnabled } from "@/accounts-payable/use-receipts-enabled";
import {
  AP_TAB,
  invoiceReviewPath,
  receiptReviewPath,
} from "@/accounts-payable/utils";
import { getErrorMessage } from "@/core/api/errors";
import { useBusiness } from "@/core/business";
import { useDebounce } from "@/core/hooks";
import { CommandLoadMore, CommandNestedState } from "./nested-list";
import type { CommandPage } from "./pages";

const documentName = (
  vendor: string | null | undefined,
  number: string | null | undefined,
  fallback: string,
) => [vendor, number].filter(Boolean).join(" ") || fallback;

export const FindReceiptItem = ({
  onOpen,
}: {
  onOpen: (page: CommandPage) => void;
}) => {
  const receiptsEnabled = useReceiptsEnabled();
  if (!receiptsEnabled) return null;

  return (
    <CommandItem
      value="find receipt receipts"
      onSelect={() => onOpen({ kind: "ap-receipts" })}
    >
      <span>Find receipt</span>
      <CommandShortcut>
        <CaretRightIcon />
      </CommandShortcut>
    </CommandItem>
  );
};

export const ApSearchPage = ({
  kind,
  query,
  onNavigate,
}: {
  kind: "ap-invoices" | "ap-receipts";
  query: string;
  onNavigate: (to: string) => void;
}) => {
  const { businessId } = useBusiness();
  const receiptsEnabled = useReceiptsEnabled();
  const search = useDebounce(query.trim(), 300);
  const invoicesOn = kind === "ap-invoices" && search.length > 0;
  const receiptsOn =
    kind === "ap-receipts" && receiptsEnabled && search.length > 0;
  const invoices = useGetInvoices(
    businessId,
    { search },
    { enabled: invoicesOn },
  );
  const receipts = useGetReceipts(
    businessId,
    { search },
    { enabled: receiptsOn },
  );
  const list = kind === "ap-invoices" ? invoices : receipts;
  const rows = useMemo(
    () =>
      kind === "ap-invoices"
        ? (invoices.data?.pages.flatMap((page) => page.items) ?? [])
        : (receipts.data?.pages.flatMap((page) => page.items) ?? []),
    [kind, invoices.data, receipts.data],
  );
  const noun = kind === "ap-invoices" ? "invoice" : "receipt";

  if (kind === "ap-receipts" && !receiptsEnabled) {
    return (
      <CommandNestedState
        heading="Receipts"
        loading={false}
        error={null}
        empty="Receipts aren't turned on."
        count={0}
      />
    );
  }

  if (!search) {
    return (
      <CommandNestedState
        heading={kind === "ap-invoices" ? "Invoices" : "Receipts"}
        loading={false}
        error={null}
        empty={
          kind === "ap-invoices"
            ? "Type an invoice number or vendor name."
            : "Type a receipt number or vendor name."
        }
        count={0}
      />
    );
  }

  return (
    <CommandNestedState
      heading={kind === "ap-invoices" ? "Invoices" : "Receipts"}
      loading={list.isPending}
      error={
        list.isError
          ? `Couldn't load ${noun}s. ${getErrorMessage(list.error)}`
          : null
      }
      onRetry={() => void list.refetch()}
      empty={`No ${noun}s match.`}
      count={rows.length}
    >
      {rows.map((row) => {
        const label = documentName(
          row.vendor_name,
          row.document_number,
          noun === "invoice" ? "Invoice" : "Receipt",
        );
        return (
          <CommandItem
            key={row.id}
            value={`${label} ${row.id}`}
            onSelect={() =>
              onNavigate(
                noun === "invoice"
                  ? invoiceReviewPath(row.id, AP_TAB.invoices)
                  : receiptReviewPath(row.id, AP_TAB.receipts),
              )
            }
          >
            <span>{label}</span>
          </CommandItem>
        );
      })}
      {list.hasNextPage && (
        <CommandLoadMore
          loading={list.isFetchingNextPage}
          onLoad={() => void list.fetchNextPage()}
        />
      )}
    </CommandNestedState>
  );
};

export const ApReviewPage = ({
  onNavigate,
}: {
  onNavigate: (to: string) => void;
}) => {
  const { businessId } = useBusiness();
  const queue = useGetReviewQueue(businessId);
  const rows = useMemo(
    () => queue.data?.pages.flatMap((page) => page.items) ?? [],
    [queue.data],
  );

  return (
    <CommandNestedState
      heading="Review"
      loading={queue.isLoading}
      error={
        queue.isError
          ? `Couldn't load the review queue. ${getErrorMessage(queue.error)}`
          : null
      }
      onRetry={() => void queue.refetch()}
      empty="No invoices to review."
      count={rows.length}
    >
      {rows.map((invoice) => {
        const label = documentName(
          invoice.vendor_name,
          invoice.document_number,
          "Invoice",
        );
        return (
          <CommandItem
            key={invoice.id}
            value={`${label} ${invoice.id}`}
            onSelect={() =>
              onNavigate(invoiceReviewPath(invoice.id, AP_TAB.review))
            }
          >
            <span>{label}</span>
          </CommandItem>
        );
      })}
      {queue.hasNextPage && (
        <CommandLoadMore
          loading={queue.isFetchingNextPage}
          onLoad={() => void queue.fetchNextPage()}
        />
      )}
    </CommandNestedState>
  );
};
